import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const STATIC = [
  { grade: 'Nursery', badge: 'Early Years', badgeColor: 'bg-pink-500', file: 'NURSERY-BOOKLIST-2025-26.pdf' },
  { grade: 'KG', badge: 'Early Years', badgeColor: 'bg-pink-500', file: 'KG-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 1', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-1-BOOKLIST-202526.pdf' },
  { grade: 'Grade 2', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-2-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 3', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-3-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 4', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-4-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 5', badge: 'Primary', badgeColor: 'bg-primary', file: 'GRADE-5-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 6', badge: 'Middle', badgeColor: 'bg-green-600', file: 'GRADE-6-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 7', badge: 'Middle', badgeColor: 'bg-green-600', file: 'GRADE-7-BOOKLIST-2025-26.pdf' },
  { grade: 'Grade 8', badge: 'Middle', badgeColor: 'bg-green-600', file: 'GRADE-8-BOOKLIST-2025-26.pdf' },
];

const Booklist = () => {
  const [booklists, setBooklists] = useState(STATIC);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/admin/api/booklist.php');
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.data?.length) setBooklists(data.data);
        }
      } catch { /* use static */ } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getCategoryColor = (cat) => {
    const c = cat || '';
    if (c.toLowerCase().includes('early')) return 'bg-pink-500';
    if (c.toLowerCase().includes('primary')) return 'bg-primary';
    if (c.toLowerCase().includes('middle')) return 'bg-green-600';
    return 'bg-gray-400';
  };

  const categories = ['All', 'Early Years', 'Primary', 'Middle'];
  const filtered = filter === 'All' ? booklists : booklists.filter(b => (b.category || b.badge) === filter);

  return (
    <div className="page-enter">
      <Helmet>
        <title>Book Lists — PAK School &amp; College System | Required Books &amp; Materials</title>
        <meta name="description" content="Download PAK School & College System's booklists for all grade levels. Find required textbooks and study materials for each class." />
        <link rel="canonical" href="https://pakschoolandcollege.com/booklist" />
      </Helmet>

      <div className="page-hero">
        <div className="page-hero-content">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-5">
            📚 Academic Resources
          </div>
          <h1>Book <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">Lists</span></h1>
          <p>Download the required textbooks and study materials for each grade level</p>
        </div>
      </div>

      <section className="py-14 sm:py-18 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Category filters */}
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full font-poppins font-600 text-sm transition-all duration-200
                  ${filter === cat ? 'bg-primary text-white shadow-md shadow-primary/25' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <i className="fas fa-book text-4xl text-gray-200 mb-4" />
              <p className="text-gray-400 font-inter">No booklists found for this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filtered.map((bl, i) => (
                <div key={i} className="group bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <i className="fas fa-file-pdf text-primary" />
                    </div>
                    <span className={`${bl.badgeColor || getCategoryColor(bl.category || bl.badge)} text-white text-[10px] font-poppins font-700 px-2 py-1 rounded-full`}>
                      {bl.category || bl.badge}
                    </span>
                  </div>
                  <h3 className="font-poppins font-700 text-navy text-sm mb-0.5">{bl.grade}</h3>
                  <p className="text-gray-400 text-xs font-inter mb-4">2025–26</p>
                  <a
                    href={bl.file_path ? `./admin/assets/booklist/${bl.file_path}` : `./admin/assets/booklist/${bl.file}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 w-full py-2 rounded-xl bg-primary text-white text-xs font-poppins font-600 hover:bg-primary-dark transition-colors"
                  >
                    <i className="fas fa-download text-[10px]" /> Download
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Tips box */}
          <div className="mt-12 bg-gradient-to-r from-primary/5 to-accent/5 rounded-2xl p-6 sm:p-8 border border-gray-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <i className="fas fa-lightbulb text-primary text-lg" />
              </div>
              <div>
                <h3 className="font-poppins font-700 text-navy text-base mb-3">Book Purchase Tips</h3>
                <ul className="text-gray-500 font-inter text-sm space-y-1.5">
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />Purchase books from authorized school bookstores for guaranteed editions</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />Check for used book options to save costs while maintaining quality</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />Ensure you have the correct edition and ISBN as listed</li>
                  <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />Contact the school office for any clarifications on required materials</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Booklist;
